// components/hauler/VerificationBlock.js
// Inline verification UI for in_progress jobs.
//
// Hauler picks the actually-delivered tier, enters a staging note,
// and captures a completion photo. When all three are filled,
// "Confirm completion" enables and the parent opens CompletionModal.
//
// LEU numerics are never shown. Tier display uses canonical labels.

import React from "react";
import { getCustomerTierLabel } from "../../utils/common";

const COLORS = {
  text: "var(--hl-text)",
  sub: "var(--hl-text-sub)",
  muted: "var(--hl-text-muted)",
  card: "var(--hl-card)",
  border: "var(--hl-border)",
  green: "var(--hl-green)",
  sky: "var(--hl-sky)",
};

// Labels derived from the canonical helper so they can never drift.
const TIER_OPTIONS = ["Small", "Half", "ThreeQ", "Full"].map((key) => ({
  key,
  label: getCustomerTierLabel(key),
}));

export default function VerificationBlock({
  verifiedTier,
  onChangeVerifiedTier,
  staging,
  onChangeStaging,
  photoFile,
  photoPreviewUrl,
  onTriggerCapture,
  // Bag count (Gap 4) — surfaced only on Bagged Trash jobs.
  isBagJob = false,
  bagCount = "",
  onChangeBagCount,
}) {
  return (
    <>
      <Section title="Verify load size">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
          {TIER_OPTIONS.map(opt => {
            const active = verifiedTier === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => onChangeVerifiedTier && onChangeVerifiedTier(opt.key)}
                style={{
                  padding: "12px 6px",
                  borderRadius: 10,
                  border: `1px solid ${active ? COLORS.sky : COLORS.border}`,
                  background: active ? "rgba(61,144,217,.18)" : "var(--hl-input-bg)",
                  color: active ? COLORS.sky : COLORS.text,
                  fontFamily: "inherit",
                  fontSize: 13,
                  fontWeight: active ? 800 : 600,
                  cursor: "pointer",
                  minHeight: 48,
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 8 }}>
          Pick the tier that matches what you actually hauled.
        </div>
      </Section>

      {isBagJob && (
        <Section title="Bag count">
          <input
            type="number"
            inputMode="numeric"
            min="0"
            value={bagCount}
            onChange={(e) => onChangeBagCount && onChangeBagCount(e.target.value)}
            placeholder="How many bags?"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              background: "var(--hl-input-bg)",
              color: COLORS.text,
              fontFamily: "inherit",
              fontSize: 14,
              outline: "none",
            }}
          />
          <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
            Helps the system flag misrepresentation when actual count differs from booked.
          </div>
        </Section>
      )}

      <Section title="Staging location">
        <input
          type="text"
          value={staging}
          onChange={(e) => onChangeStaging && onChangeStaging(e.target.value)}
          placeholder="e.g. driveway, side yard, garage entrance"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: 10,
            border: `1px solid ${COLORS.border}`,
            background: "var(--hl-input-bg)",
            color: COLORS.text,
            fontFamily: "inherit",
            fontSize: 14,
            outline: "none",
          }}
        />
        <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
          Where the load was staged before you picked it up.
        </div>
      </Section>

      <Section title="Completion photo">
        {photoPreviewUrl ? (
          <div>
            <img
              src={photoPreviewUrl}
              alt="Completion preview"
              style={{
                width: "100%",
                boxSizing: "border-box",
                maxHeight: 220,
                objectFit: "cover",
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
              }}
            />
            <button
              onClick={() => onTriggerCapture && onTriggerCapture()}
              style={{
                marginTop: 8,
                width: "100%",
                boxSizing: "border-box",
                padding: "10px",
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
                background: "transparent",
                color: COLORS.sky,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                minHeight: 44,
              }}
            >
              Retake photo
            </button>
          </div>
        ) : (
          <button
            onClick={() => onTriggerCapture && onTriggerCapture()}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "20px",
              borderRadius: 10,
              border: `2px dashed ${COLORS.border}`,
              background: "var(--hl-input-bg)",
              color: COLORS.sub,
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              minHeight: 80,
            }}
          >
            Tap to capture · live camera only
          </button>
        )}
        <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 6 }}>
          Live capture only — gallery uploads are not accepted. Photo corroborates the customer's pre-pickup capture.
        </div>
      </Section>
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{
      background: COLORS.card,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 14,
      padding: "14px 14px",
      marginBottom: 10,
    }}>
      <div style={{ fontSize: 11, letterSpacing: 1.5, fontWeight: 700, color: COLORS.sub, textTransform: "uppercase", marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// Canonical mapping used when calling completeJob — kept beside
// VerificationBlock so the picker labels and the LEU values stay
// in sync.
export const VERIFIED_TIER_KEYS = TIER_OPTIONS.map(t => t.key);
export const VERIFIED_TIER_LABEL = Object.fromEntries(TIER_OPTIONS.map(t => [t.key, t.label]));
